import dayjs, { Dayjs, OptionType, UnitType } from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/es-us';
import 'dayjs/locale/en-ca';
import 'dayjs/locale/fr-ca';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration, { Duration, DurationInputType } from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import isNil from 'ramda/src/isNil';
import { isSafari } from 'react-device-detect';
import { Config } from '~web/config';
import { i18n, Locale, t } from '~web/i18n';
export { Dayjs } from 'dayjs';

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(duration);

/**
 * Removes milliseconds
 * @param granularity
 * @param dates
 */
const removeMs = (granularity: UnitType, ...dates: Dayjs[]): void => {
  if (granularity !== 'millisecond') {
    dates.forEach((date) => dayjs(date).millisecond(0));
  }
};

export const datetime = {
  parse: (value: string, format?: OptionType, strict = false): Dayjs => {
    if (isNil(value)) {
      return null;
    }
    // Safari 13 can't parse correctly if format isn't specified
    const date = dayjs(
      value,
      isNil(format) && isSafari ? Config.dateTimeFormats.server : format,
      strict,
    );
    if (!date.isValid()) {
      return null;
    }
    return date;
  },
  isLive: (startDate: Dayjs, endDate: Dayjs, granularity: UnitType = 'second'): boolean => {
    if (isNil(startDate) || isNil(endDate)) {
      return false;
    }
    removeMs(granularity, startDate, endDate);
    return dayjs().isBetween(startDate, endDate, granularity, '[]');
  },
  isUpcoming: (date: Dayjs, granularity: UnitType = 'second'): boolean => {
    if (isNil(date)) {
      return false;
    }
    removeMs(granularity, date);
    return dayjs().isBefore(date, granularity);
  },
  isUpcomingOrNow: (date: Dayjs, granularity: UnitType = 'second'): boolean => {
    if (isNil(date)) {
      return false;
    }
    removeMs(granularity, date);
    return dayjs().isSameOrBefore(date, granularity);
  },
  isPast: (date: Dayjs, granularity: UnitType = 'second'): boolean => {
    if (isNil(date)) {
      return false;
    }
    removeMs(granularity, date);
    return dayjs().isAfter(date, granularity);
  },
  isPastOrNow: (date: Dayjs, granularity: UnitType = 'second'): boolean => {
    if (isNil(date)) {
      return false;
    }
    removeMs(granularity, date);
    return dayjs().isSameOrAfter(date, granularity);
  },
  now: (): Dayjs => dayjs(),
  isToday: (date: Dayjs, granularity: UnitType = 'day'): boolean => {
    if (isNil(date)) {
      return false;
    }
    removeMs(granularity, date);
    return date.isSame(datetime.now(), granularity);
  },
  isSameDay: (date: Dayjs, otherDate: Dayjs, granularity: UnitType = 'day'): boolean => {
    if (isNil(date) || isNil(otherDate)) {
      return false;
    }
    removeMs(granularity, date, otherDate);
    return date.isSame(otherDate, granularity);
  },
  isSameMonth: (date: Dayjs, otherDate: Dayjs, granularity: UnitType = 'month'): boolean => {
    if (isNil(date) || isNil(otherDate)) {
      return false;
    }
    removeMs(granularity, date, otherDate);
    return date.isSame(otherDate, granularity);
  },
  diff: (date: Dayjs, otherDate: Dayjs, unit: UnitType = 'second', precise?: boolean): number => {
    removeMs(unit, date, otherDate);
    return date.diff(otherDate, unit, precise);
  },
  max: (date: Dayjs, otherDate: Dayjs, granularity: UnitType = 'second'): Dayjs => {
    if (isNil(date) || isNil(otherDate)) {
      return null;
    }
    removeMs(granularity, date, otherDate);
    return date.isAfter(otherDate) ? date : otherDate;
  },
  min: (date: Dayjs, otherDate: Dayjs, granularity: UnitType = 'second'): Dayjs => {
    if (isNil(date) || isNil(otherDate)) {
      return null;
    }
    removeMs(granularity, date, otherDate);
    return date.isBefore(otherDate) ? date : otherDate;
  },
  maxString: (
    dateString: string,
    otherDateString: string,
    granularity: UnitType = 'second',
  ): string => {
    const date = datetime.parse(dateString);
    const otherDate = datetime.parse(otherDateString);
    if (isNil(date) || isNil(otherDate)) {
      return null;
    }
    removeMs(granularity, date, otherDate);
    return date.isAfter(otherDate) ? dateString : otherDateString;
  },
  minString: (
    dateString: string,
    otherDateString: string,
    granularity: UnitType = 'second',
  ): string => {
    const date = datetime.parse(dateString);
    const otherDate = datetime.parse(otherDateString);
    if (isNil(date) || isNil(otherDate)) {
      return null;
    }
    removeMs(granularity, date, otherDate);
    return date.isBefore(otherDate) ? dateString : otherDateString;
  },
  getTimeLeft: (date: Dayjs, unit: UnitType = 'second'): number => {
    const now = datetime.now();
    removeMs(unit, now, date);
    return date.diff(now, unit);
  },
  duration: (input: DurationInputType, unit?: UnitType): Duration => dayjs.duration(input, unit),
  stringifyDateRange: (date1: Dayjs, date2: Dayjs): string => {
    if (date1.isSame(date2, 'day')) {
      return date1.format('MMM D');
    }
    if (date1.month() === date2.month()) {
      return `${date1.format('MMM D')} - ${date2.format('D')}`;
    }
    return `${date1.format('MMM D')} - ${date2.format('MMM D')}`;
  },
};

export enum DateTimeFormatType {
  LONG,
  MEDIUM,
  SHORT,
}

export const getTimeZoneAbbr = (): string => {
  const currentZone = dayjs.tz.guess();
  const zoneAbbr = dayjs().tz(currentZone).format('z');
  const key = `timezone.${zoneAbbr}`;
  return i18n.exists(key) ? t(key) : zoneAbbr;
};

// locales for date/time formats have to imported here in datetime.js
export const localizeDateTime = (
  dateTime: Dayjs,
  formatType: DateTimeFormatType,
): string | null => {
  if (isNil(dateTime)) {
    return null;
  }

  const localeDateTime = dateTime.locale(i18n.language.toLocaleLowerCase());
  const zone = getTimeZoneAbbr();
  switch (i18n.language) {
    case Locale.FRENCH_CA:
      if (formatType === DateTimeFormatType.LONG) {
        return localeDateTime.format(`ddd, D MMM, HH:mm [${zone}]`);
      } else if (formatType === DateTimeFormatType.MEDIUM) {
        return localeDateTime.format('ddd, D MMM, HH:mm');
      } else if (formatType === DateTimeFormatType.SHORT) {
        return localeDateTime.format('D MMM, HH:mm');
      } else {
        throw Error(`Passed format type is not supported by this function.`);
      }
    case Locale.SPANISH_US:
      if (formatType === DateTimeFormatType.LONG) {
        return localeDateTime.format(`ddd, MMM D, HH:mm [${zone}]`);
      } else if (formatType === DateTimeFormatType.MEDIUM) {
        return localeDateTime.format('ddd, MMM D, HH:mm');
      } else if (formatType === DateTimeFormatType.SHORT) {
        return localeDateTime.format('MMM D, HH:mm');
      } else {
        throw Error(`Passed format type is not supported by this function.`);
      }
    case Locale.ENGLISH_US:
    case Locale.ENGLISH_CA:
    default:
      if (formatType === DateTimeFormatType.LONG) {
        return localeDateTime.format(`ddd, MMM D, h:mma [${zone}]`);
      } else if (formatType === DateTimeFormatType.MEDIUM) {
        return localeDateTime.format('ddd, MMM D, h:mma');
      } else if (formatType === DateTimeFormatType.SHORT) {
        return localeDateTime.format('MMM D, h:mma');
      } else {
        throw Error(`Passed format type is not supported by this function.`);
      }
  }
};

export const localizeDate = (date: Dayjs, formatType: DateTimeFormatType): string | null => {
  if (isNil(date)) {
    return null;
  }

  const localeLowerCase = i18n.language.toLocaleLowerCase();
  const localDate = date.locale(localeLowerCase);
  switch (i18n.language) {
    case Locale.FRENCH_CA:
      if (formatType === DateTimeFormatType.LONG) {
        return localDate.format('D MMM, YYYY');
      } else if (formatType === DateTimeFormatType.MEDIUM) {
        return localDate.format('ddd, D MMM');
      } else if (formatType === DateTimeFormatType.SHORT) {
        return localDate.format('D MMM');
      } else {
        throw Error(`Passed format type is not supported by this function.`);
      }
    case Locale.ENGLISH_US:
    case Locale.ENGLISH_CA:
    case Locale.SPANISH_US:
    default:
      if (formatType === DateTimeFormatType.LONG) {
        return localDate.format('MMM D, YYYY');
      } else if (formatType === DateTimeFormatType.MEDIUM) {
        return localDate.format('ddd, MMM D');
      } else if (formatType === DateTimeFormatType.SHORT) {
        return localDate.format('MMM D');
      } else {
        throw Error(`Passed format type is not supported by this function.`);
      }
  }
};

export const localizeTime = (time: Dayjs, formatType: DateTimeFormatType): string | null => {
  if (isNil(time)) {
    return null;
  }

  const localeLowerCase = i18n.language.toLocaleLowerCase();
  const localTime = time.locale(localeLowerCase);
  switch (i18n.language) {
    case Locale.FRENCH_CA:
    case Locale.SPANISH_US:
      if (formatType === DateTimeFormatType.LONG) {
        return localTime.format('HH:mm::ss');
      } else if (formatType === DateTimeFormatType.SHORT) {
        return localTime.format('HH:mm');
      } else {
        throw Error(`Passed format type is not supported by this function.`);
      }
    case Locale.ENGLISH_US:
    case Locale.ENGLISH_CA:
    default:
      if (formatType === DateTimeFormatType.LONG) {
        return localTime.format('h:mm::ss a');
      } else if (formatType === DateTimeFormatType.SHORT) {
        return localTime.format('h:mma');
      } else {
        throw Error(`Passed format type is not supported by this function.`);
      }
  }
};

export const localizeDateRange = (
  startDate: Dayjs,
  endDate: Dayjs,
  formatType: DateTimeFormatType,
): string | null => {
  if (isNil(startDate) || isNil(endDate)) {
    return null;
  }

  if (datetime.isSameDay(startDate, endDate)) {
    return localizeDate(startDate, formatType);
  }
  // When requesting the short format, this returns Jan 1 - 2 or Jan 31 - Feb 2 depending
  // on whether the days are in the same month. In the longer formats, we always print the
  // whole date - e.g. Sat, Jan 1 - Sun, Jan 2.
  const shortFormatSameMonth =
    formatType === DateTimeFormatType.SHORT && datetime.isSameMonth(startDate, endDate);

  // English: Jan 10 - Feb 15 / Jan 10 - 15
  // French: 10 janv. - 15 fév. / 10 - 15 fév.
  return t('duration.dateRange', {
    startDate:
      shortFormatSameMonth && i18n.language === Locale.FRENCH_CA
        ? startDate.format('D')
        : localizeDate(startDate, formatType),
    endDate:
      shortFormatSameMonth &&
      (i18n.language === Locale.ENGLISH_US ||
        i18n.language === Locale.ENGLISH_CA ||
        i18n.language === Locale.SPANISH_US)
        ? endDate.format('D')
        : localizeDate(endDate, formatType),
  });
};

/**
 * Returns the most commonly used date format based on locale passed
 * if no locale passed returns ISO 8601 date format
 * @param locale - optional locale that determines returned format
 * @param formatType - optional date format parameter
 */
export const localeBasedDateFormat = (locale?: string, formatType?: DateTimeFormatType): string => {
  switch (locale as Locale) {
    case Locale.ENGLISH_US:
    case Locale.ENGLISH_CA:
    case Locale.SPANISH_US:
      if (formatType === DateTimeFormatType.SHORT) {
        return 'MM/DD';
      } else {
        return 'MM/DD/YYYY';
      }
    case Locale.FRENCH_CA:
      if (formatType === DateTimeFormatType.SHORT) {
        return 'DD/MM';
      } else {
        return 'DD/MM/YYYY';
      }
    default:
      // Official ISO 8601 standards for short/long dates
      if (formatType === DateTimeFormatType.SHORT) {
        return '--MM-DD';
      } else {
        return 'YYYY-MM-DD';
      }
  }
};

export const isoDateToLocaleBased = (date: string): string => {
  const parsedDate = datetime.parse(date, localeBasedDateFormat(), true);
  return parsedDate ? parsedDate.format(localeBasedDateFormat(i18n.language)) : null;
};

export const localeBasedDateToISO = (date: string): string => {
  const parsedDate = datetime.parse(date, localeBasedDateFormat(i18n.language), true);
  return parsedDate ? parsedDate.format(localeBasedDateFormat()) : null;
};



// WEBPACK FOOTER //
// /codebuild/output/src891534606/src/modules/common/src/helper/datetime.ts